import { useCallback } from 'react';
import { TOOL_GROUPS, type ToolSelection } from '../../types/tools';
import ToolGroupCard from './ToolGroupCard';
import styles from './ToolSelector.module.css';

interface ToolSelectorProps {
  value: ToolSelection;
  onChange: (selection: ToolSelection) => void;
  readOnly?: boolean;
  /** Number of environments selected — shows hint on Environment Runtime group when 0 */
  environmentCount?: number;
}

export default function ToolSelector({ value, onChange, readOnly, environmentCount }: ToolSelectorProps) {
  const handleToggle = useCallback(
    (toolId: string, enabled: boolean) => {
      onChange({ ...value, [toolId]: { ...value[toolId], enabled } });
    },
    [value, onChange],
  );

  const handleGroupToggle = useCallback(
    (groupId: string, enabled: boolean) => {
      const group = TOOL_GROUPS.find((g) => g.id === groupId);
      if (!group) return;
      const updates: ToolSelection = { ...value };
      for (const tool of group.tools) {
        if (!tool.alwaysEnabled) {
          updates[tool.id] = { ...updates[tool.id], enabled };
        }
      }
      onChange(updates);
    },
    [value, onChange],
  );

  return (
    <div className={styles.grid}>
      {TOOL_GROUPS.map((group) => (
        <ToolGroupCard
          key={group.id}
          group={group}
          selection={value}
          onChange={handleToggle}
          onGroupToggle={handleGroupToggle}
          readOnly={readOnly}
          hint={group.id === 'environment' && environmentCount === 0
            ? 'Select environments in the Environments step to enable runtime access'
            : undefined}
        />
      ))}
    </div>
  );
}
